import type { CipComponentEntity } from "building-types/floors/cipComponentTypes";
import { MasonryType, type MasonryComponentEntity } from "building-types/floors/masonryComponentTypes";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { WallPlaneRuleHandle } from "building-types/handles/docEntityHandleTypes";
import { CMUProfileToNominalWidth } from "building-types/masonry/masonryTypes";
import type { WallPlaneAssemblyLayer, WallPlaneMaterialFinish } from "building-types/walls2/wallPlaneAssemblyTypes";
import type {
  WallAssemblyFinishLayer,
  WallAssemblyLayerParentHandle,
  WallAssemblyMaterialCatalogLayer,
  WallAssemblyMaterialCatalogLayerType,
} from "building-types/walls/wallAssemblyTypes";
import { WallCladdingType } from "building-types/walls/wallDesciptorTypes";
import type { StructuralMaterialBase } from "building-types/walls/wallMaterialTypes";
import { WallMaterialType } from "building-types/walls/wallMaterialTypesEnum";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { getFramingProfileDescriptor } from "doc/utils/lumberTools";
import {
  wallAssemblyExteriorFinishLayerNew,
  wallAssemblyInteriorFinishLayerNew,
  wallAssemblyMaterialCatalogLayerNew,
} from "../wallAssemblies/wallAssembly";

// In order for WALLS V2 to be compatible with legacy wall generation, we need to keep
// the return types of the material catalog components in sync with the return types of the wall assembly
// layers. This file contains the functions to convert between the two.

type WallPlaneAssemblyComponent =
  | SheathingComponentEntity
  | FramingComponentEntity
  | MasonryComponentEntity
  | CipComponentEntity;

export function wallPlaneAssemblyComponentToLegacyStructuralMaterial(
  component: WallPlaneAssemblyComponent,
): StructuralMaterialBase<WallAssemblyMaterialCatalogLayerType> {
  let componentThickness = 0;
  switch (component.entityType) {
    case "FRAMING_COMPONENT": {
      componentThickness = getFramingProfileDescriptor(component).width;
      break;
    }
    case "MASONRY_COMPONENT": {
      componentThickness =
        component.masonryType === MasonryType.CONCRETE ? CMUProfileToNominalWidth[component.profile] : component.width;
      break;
    }
    case "CIP_COMPONENT": {
      componentThickness = component.thickness;
      break;
    }
    case "SHEATHING_COMPONENT": {
      componentThickness = component.thickness;
      break;
    }
    default: {
      assertUnreachable(component);
    }
  }
  return {
    catalogItemType: "STRUCTURAL_MATERIAL",
    type: wallPlaneAssemblyComponentToLegacyMaterialCatalogLayerType(component),
    name: component.name,
    thickness: componentThickness,
  };
}

export function wallPlaneAssemblyComponentToLegacyMaterialCatalogLayerType(
  component: WallPlaneAssemblyComponent,
): WallAssemblyMaterialCatalogLayerType {
  switch (component.entityType) {
    case "FRAMING_COMPONENT": {
      return WallMaterialType.STRUCTURE;
    }
    case "MASONRY_COMPONENT": {
      return WallMaterialType.STRUCTURE;
    }
    case "CIP_COMPONENT": {
      return WallMaterialType.STRUCTURE;
    }
    case "SHEATHING_COMPONENT": {
      return WallMaterialType.SUBSTRATE;
    }
    default: {
      assertUnreachable(component);
    }
  }
}

export function wallPlaneAssemblyLayerToLegacyMaterialCatalogLayer(
  layer: WallPlaneAssemblyLayer,
  layerSource: WallPlaneRuleHandle | undefined,
  parentChain?: WallAssemblyLayerParentHandle[],
): WallAssemblyMaterialCatalogLayer {
  switch (layer.layerType) {
    case "FRAMING": {
      return wallAssemblyMaterialCatalogLayerNew({
        wallMaterialId: layer.framingComponentId,
        type: wallPlaneAssemblyLayerToLegacyWallMaterialType(layer),
        layerSource,
        parentChain,
      });
    }
    case "MASONRY": {
      return wallAssemblyMaterialCatalogLayerNew({
        wallMaterialId: layer.masonryComponentId,
        type: wallPlaneAssemblyLayerToLegacyWallMaterialType(layer),
        layerSource,
        parentChain,
      });
    }
    case "CAST_IN_PLACE": {
      return wallAssemblyMaterialCatalogLayerNew({
        wallMaterialId: layer.cipComponentId,
        type: wallPlaneAssemblyLayerToLegacyWallMaterialType(layer),
        layerSource,
        parentChain,
      });
    }
    case "SHEATHING": {
      return wallAssemblyMaterialCatalogLayerNew({
        wallMaterialId: layer.sheathingComponentId,
        type: wallPlaneAssemblyLayerToLegacyWallMaterialType(layer),
        layerSource,
        parentChain,
      });
    }
    default: {
      assertUnreachable(layer);
    }
  }
}

export function wallPlaneAssemblyLayerToLegacyWallMaterialType(
  layer: WallPlaneAssemblyLayer,
): WallAssemblyMaterialCatalogLayerType {
  switch (layer.layerType) {
    case "FRAMING": {
      return WallMaterialType.STRUCTURE;
    }
    case "MASONRY": {
      return WallMaterialType.STRUCTURE;
    }
    case "CAST_IN_PLACE": {
      return WallMaterialType.STRUCTURE;
    }
    case "SHEATHING": {
      return WallMaterialType.SUBSTRATE;
    }
    default: {
      assertUnreachable(layer);
    }
  }
}

type WallPlaneAssemblyLayerWithFinish = WallPlaneAssemblyLayer & {
  finish: WallPlaneMaterialFinish;
};

export function wallPlaneAssemblyLayerHasFinish(
  layer: WallPlaneAssemblyLayer,
): layer is WallPlaneAssemblyLayerWithFinish {
  return (
    (layer.layerType === "SHEATHING" || layer.layerType === "MASONRY" || layer.layerType === "CAST_IN_PLACE") &&
    layer.finish !== undefined
  );
}

export function wallPlaneAssemblyLayerToLegacyFinishLayer(
  layer: WallPlaneAssemblyLayerWithFinish,
  layerSource: WallPlaneRuleHandle | undefined,
  parentChain?: WallAssemblyLayerParentHandle[],
): WallAssemblyFinishLayer {
  // backup check to ensure the guard above was used and this isn't just a cast
  assertIsNotNullOrUndefined(layer.finish, "Layer must have a finish");
  switch (layer.finish.finishType) {
    case "EXTERIOR": {
      return wallAssemblyExteriorFinishLayerNew({
        finishMaterial: layer.finish.finishPalette,
        layerSource,
        parentChain,
      });
    }
    case "INTERIOR": {
      return wallAssemblyInteriorFinishLayerNew({
        finishMaterial: layer.finish.finishPalette,
        layerSource,
        parentChain,
      });
    }
    default: {
      assertUnreachable(layer.finish);
    }
  }
}

export function wallPlaneAssemblyLayerToLegacyWallCladdingType(layer: WallPlaneAssemblyLayer): WallCladdingType {
  if (layer.layerType === "SHEATHING" || layer.layerType === "MASONRY" || layer.layerType === "CAST_IN_PLACE") {
    if (layer.finish?.finishType === "EXTERIOR") {
      return WallCladdingType.EXTERIOR;
    } else {
      return WallCladdingType.INTERIOR;
    }
  }
  return WallCladdingType.NONE;
}
